import React, { useState, useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { getFontSize } from "../../../../AppFont";
import { ChapterEntity } from "../../../../generated/graphql";
import { ChevronRightIcon } from "@heroicons/react/outline";
import { RefdocItem2 } from "../../../../components/refdoc-item/refdoc-item2";

const INITIAL_VISIBLE_COUNT = 5;

/** When set in localStorage, show refdoc view counts (per chapter and per document). */
const showRefCount = typeof localStorage !== "undefined" ? localStorage.getItem("showrefcount") : null;

interface ReferenceLibraryCardProps {
  chapters: ChapterEntity[];
  refdocCount?: { [k: string]: number };
  initialExpandedChapterId?: number;
}

export const ReferenceLibraryCard = ({ chapters, refdocCount = {}, initialExpandedChapterId }: ReferenceLibraryCardProps) => {
  const [showAll, setShowAll] = useState(false);
  const [expandedChapters, setExpandedChapters] = useState<Set<number>>(new Set());
  const [highlightedChapterId, setHighlightedChapterId] = useState<number | null>(null);
  const visibleChapters = showAll ? chapters : chapters.slice(0, INITIAL_VISIBLE_COUNT);
  const hasMore = chapters.length > INITIAL_VISIBLE_COUNT;
  const chapterRefs = useRef<{ [key: number]: HTMLLIElement | null }>({});
  const hasScrolledRef = useRef(false);

  // Expand only when opened with ?ref_cid= (deep link); do not auto-expand on normal page load.
  useEffect(() => {
    if (chapters.length === 0 || !initialExpandedChapterId) return;
    if (!chapters.some((c) => c.id === initialExpandedChapterId)) return;

    const chapterIndex = chapters.findIndex((c) => c.id === initialExpandedChapterId);
    if (chapterIndex >= INITIAL_VISIBLE_COUNT) {
      setShowAll(true);
    }

    const timer = setTimeout(() => {
      setExpandedChapters((prev) => {
        const newSet = new Set(prev);
        newSet.add(initialExpandedChapterId);
        return newSet;
      });

      setTimeout(() => {
        setHighlightedChapterId(initialExpandedChapterId);
        setTimeout(() => {
          setHighlightedChapterId(null);
        }, 2500);
      }, 1500);
    }, 1000);

    return () => clearTimeout(timer);
  }, [chapters, initialExpandedChapterId]);

  // Separate effect to handle scrolling when chapter becomes expanded and visible (only once initially)
  useEffect(() => {
    if (
      initialExpandedChapterId && 
      expandedChapters.has(initialExpandedChapterId) && 
      !hasScrolledRef.current
    ) {
      const scrollTimer = setTimeout(() => {
        if (chapterRefs.current[initialExpandedChapterId]) {
          chapterRefs.current[initialExpandedChapterId]?.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
          hasScrolledRef.current = true; // Mark that we've scrolled
        }
      }, 500); // Wait for expansion animation and DOM updates to complete

      return () => clearTimeout(scrollTimer);
    }
  }, [initialExpandedChapterId, expandedChapters, showAll]);

  const toggleChapter = (chapterId: number) => {
    setExpandedChapters((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(chapterId)) {
        newSet.delete(chapterId);
      } else {
        newSet.add(chapterId);
      }
      return newSet;
    });
  };

  return (
    <div className="min-w-0 overflow-hidden bg-white rounded-xl shadow-sm border border-gray-100 pt-4 pb-4">
      <h2 className="font-semibold pl-5">My Reference Library</h2>
      {chapters.length === 0 ? (
        <p className="mt-2 text-gray-500 pl-5">No reference documents yet.</p>
      ) : (
        <>
          <ul className="mt-0 pt-2 divide-y divide-gray-200">
            {visibleChapters.map((chapter) => {
              const isExpanded = expandedChapters.has(chapter.id);
              const isHighlighted = highlightedChapterId === chapter.id;
              return (
                <motion.li
                  key={chapter.id}
                  ref={(el) => {
                    chapterRefs.current[chapter.id] = el;
                  }}
                  className="min-w-0 overflow-hidden first:pt-0 ml-0"
                  animate={
                    isHighlighted
                      ? {
                          backgroundColor: [
                            "rgba(255, 255, 255, 1)",
                            "rgba(59, 130, 246, 0.15)",
                            "rgba(255, 255, 255, 1)",
                            "rgba(59, 130, 246, 0.15)",
                            "rgba(255, 255, 255, 1)",
                          ],
                        }
                      : {}
                  }
                  transition={{
                    duration: 1.2,
                    repeat: 0,
                    ease: "easeInOut",
                  }}
                >
                  <div
                    onClick={() => toggleChapter(chapter.id)}
                    className="min-w-0 overflow-hidden py-3.5 flex items-center gap-2 cursor-pointer hover:bg-gray-50 pr-4"
                  >
                    <div className="min-w-0 flex-1 flex items-center gap-1 overflow-hidden pl-5 pr-2">
                      <span className={`truncate ${isExpanded ? "font-bold" : ""}`}>{chapter.chapter_name}</span>
                      <span className="shrink-0">({chapter.refdocs.length})</span>
                      {showRefCount && (
                        <span className="shrink-0 text-gray-500">
                          {chapter.refdocs.reduce(
                            (sum, doc) => sum + (refdocCount[String(doc.id)] ?? 0),
                            0
                          )}
                        </span>
                      )}
                    </div>
                    <ChevronRightIcon
                      className="h-4 w-4 shrink-0 text-gray-500 mt-0.5 transition-transform duration-200"
                      style={{
                        transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                        transformOrigin: "center",
                      }}
                    />
                  </div>
                  <AnimatePresence>
                    {isExpanded && chapter.refdocs.length > 0 && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="pb-3 space-y-1">
                          {chapter.refdocs.map((document) => (
                            <div key={document.id} className="pl-2">
                              <RefdocItem2
                                refdocCount={refdocCount}
                                id={document.id}
                                name={document.document_name}
                                url={document.url}
                                type={document.reftype || ""}
                              />
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.li>
              );
            })}
          </ul>
          {hasMore && (
            <div className="flex justify-end pt-4 pr-4">
              <button
                type="button"
                onClick={() => setShowAll(!showAll)}
                className="font-semibold focus:outline-none flex items-center"
                style={{ color: "#014ec7", fontSize: getFontSize() }}
              >
                {showAll ? "See less" : "See all reference documents"}
                <ChevronRightIcon
                  className={`h-4 w-4 ml-1 transition-transform ${
                    showAll ? "rotate-90" : ""
                  }`}
                />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
